import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setDrawerOpen } from 'features/ui/model/uiSlice';
import CloseIcon from '@mui/icons-material/Close';
import {
  Drawer,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Button,
  Box,
  FormControl,
  Autocomplete,
} from '@mui/material';
import { DrawerContent } from 'shared/ui/Drawer/DrawerStyles';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { useGetCitiesQuery } from 'features/getCities/api/getCitiesApi';
import { useUpdateParticipantMutation } from 'features/createParticipant/createParticipant';
import { useGetTestQuery } from 'features/getParticipants/api/getParticipantsApi';

type DrawerShopDotsChangePropsType = {
  shopDots: any;
};

type FormDataType = {
  title?: string;
  city_id?: string | number;
  direction?: string | number;
  door_code?: string | number;
  id_1c?: string | number;
  status?: string | number; // изменение типа
  tier?: string | number;
  address?: string;
};

const tierList = [
  {
    value: 'TOP',
    id: 0,
  },
  {
    value: 'MIDDLE',
    id: 1,
  },
  {
    value: 'LOW',
    id: 2,
  },
];
const statusList = [
  {
    value: 'ACTIVE',
    id: 0,
  },
  {
    value: 'BLOCKED',
    id: 1,
  },
];

const directionList = [
  {
    value: 'KAM',
    id: 0,
  },
  {
    value: 'DS',
    id: 1,
  },
  {
    value: 'OP',
    id: 2,
  },
];
export const DrawerShopDotsChange = ({ shopDots }: DrawerShopDotsChangePropsType) => {
  const dispatch = useAppDispatch();
  const { data } = useGetTestQuery(shopDots.id);
  const { isDrawerOpen, actionDrawer } = useAppSelector((state) => state.ui);
  const { data: cities, isSuccess: citiesListSuccess } = useGetCitiesQuery({ sort: 'country_id,name_ru' });
  const [updateRequest] = useUpdateParticipantMutation();

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    reset,
    formState: { errors: formErrors },
  } = useForm<FormDataType>({
    defaultValues: {
      address: '',
      city_id: '',
      direction: '',
      door_code: '',
      id_1c: '',
      status: '',
      tier: '',
      title: '',
    },
  });

  const onSubmit: SubmitHandler<FormDataType> = () => {
    const errors = Object.keys(formErrors).length > 0;
    if (errors) {
      return;
    }
    const data = getValues();
    updateRequest({
      id: shopDots.id,
      body: {
        title: data.title,
        status: data.status,
        direction: data.direction,
        tier: data.tier,
        id_1c: data.id_1c,
        door_code: data.door_code,
        city_id: data.city_id,
        address: data.address,
      },
    }).then(() => {
      dispatch(setDrawerOpen(false));
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    });
  };

  useEffect(() => {
    reset(data || {});
  }, [data, reset]);

  const cityIdWatch = watch('city_id');

  const selectCity = cities?.find((city) => city.id === cityIdWatch);

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => dispatch(setDrawerOpen(false))}
    >
      <DrawerContent style={{ width: '800px' }}>
        <div className="header">
          <Typography
            variant="h5"
            sx={{ fontWeight: '600' }}
          >
            {actionDrawer === 'create' ? 'Создание торговой точки' : 'Изменение торговой точки'}
          </Typography>
          <IconButton
            onClick={() => dispatch(setDrawerOpen(false))}
            sx={{ color: '#000' }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="content">
          <div>
            <div style={{ marginBottom: '20px' }}>Адресная информация</div>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  type="text"
                  label="Адрес"
                  placeholder=""
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    marginBottom: '24px',
                  }}
                  error={formErrors.title && true}
                  {...field}
                />
              )}
            />

            <Controller
              name="door_code"
              control={control}
              render={({ field }) => (
                <TextField
                  type="text"
                  label="Код двери"
                  placeholder=""
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    marginBottom: '24px',
                  }}
                  error={formErrors.door_code && true}
                  {...field}
                />
              )}
            />
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  type="text"
                  label="Наименование"
                  placeholder=""
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    marginBottom: '24px',
                  }}
                  error={formErrors.title && true}
                  {...field}
                />
              )}
            />
            <Controller
              control={control}
              name="city_id"
              render={({ field: { onChange } }) => (
                <FormControl
                  fullWidth
                  sx={{ marginBottom: '24px' }}
                >
                  <Autocomplete
                    options={cities || []}
                    value={selectCity || null}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(options) => options?.name_ru || options.name}
                    loading={citiesListSuccess}
                    loadingText="По вашему запросу ничего не найдено"
                    onChange={(event, value) => onChange(value?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Город"
                      />
                    )}
                  />
                </FormControl>
              )}
            />
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  type="text"
                  label="Статус"
                  placeholder=""
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    marginBottom: '24px',
                  }}
                  error={formErrors.status && true}
                  {...field}
                >
                  {statusList.map((tier) => (
                    <MenuItem value={tier.id}>{tier.value} </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="direction"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  type="text"
                  label="Канал"
                  placeholder=""
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    marginBottom: '24px',
                  }}
                  error={formErrors.status && true}
                  {...field}
                >
                  {directionList.map((tier) => (
                    <MenuItem value={tier.id + 1}>{tier.value} </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="tier"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  type="text"
                  label="Уровень"
                  placeholder=""
                  fullWidth
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    marginBottom: '24px',
                  }}
                  error={formErrors.status && true}
                  {...field}
                >
                  {tierList.map((tier) => (
                    <MenuItem value={tier.id + 1}>{tier.value} </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </div>
          <Box
            sx={{
              display: 'flex',
              marginTop: '30px',
            }}
          >
            <Button
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              sx={{ textTransform: 'none' }}
            >
              Изменить
            </Button>
          </Box>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
